package com.essntl.features.country.ui.addeditcountry

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.country.domain.model.CountryModel
import com.essntl.features.country.domain.repository.CountryRepository
import com.essntl.features.country.domain.usecase.AddEditCountryUseCase
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class AddEditCountryViewModel(
    private val countryRepository: CountryRepository,
    private val addEditCountryUseCase: AddEditCountryUseCase,
    private val areaRepository: AreaRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditCountryState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    fun onEvent(event: AddEditCountryEvent) {
        when (event) {
            is AddEditCountryEvent.GetCountry -> getCountry(event)
            is AddEditCountryEvent.UpdateCountryFields -> updateCountryFields(event)
            is AddEditCountryEvent.AddCountry -> addCountry()
            is AddEditCountryEvent.EditCountry -> editCountry()
            is AddEditCountryEvent.DeleteCountry -> deleteCountry()
            is AddEditCountryEvent.ClearState -> clearState()
        }
    }

    private var getCategoryJob: Job? = null
    private fun getCountry(event: AddEditCountryEvent.GetCountry) {
        getCategoryJob?.cancel()
        getCategoryJob = getData(
            getData = { countryRepository.get(event.id) },
            getState = { state.value.getCountry },
            setState = {
                state.value = state.value.copy(
                    getCountry = it,
                    isEditEnabled = false,
                )
            },
            onSuccess = { c ->
                state.value.country.value = c
                getCountAreas()
            },
        )
    }

    private fun getCountAreas() =
        getData(
            getData = { areaRepository.getCountAreasByCountry(state.value.country.value.id) },
            getState = { state.value.getAreasCountryCount },
            setState = { state.value = state.value.copy(getAreasCountryCount = it) },
        )

    private fun updateCountryFields(event: AddEditCountryEvent.UpdateCountryFields) {
        state.value.country.value = event.country

        val newCountry =
            state.value.getCountry.data?.getChangedFields(state.value.country.value) ?: return

        state.update { it.copy(isEditEnabled = !newCountry.isEmpty) }
    }

    private fun addCountry() =
        getData(
            getData = { addEditCountryUseCase.insertCountry(state.value.country.value) },
            getState = { state.value.addEditCountry },
            setState = { state.value = state.value.copy(addEditCountry = it) },
            onSuccess = { p ->
                state.update {
                    it.copy(
                        getCountry = State.success(p),
                    )
                }

                state.value.country.value = p

                toastManager.success(
                    description = "Country created successfully",
                )
            },
        )


    private fun editCountry() =
        getData(
            getData = { addEditCountryUseCase.updateCountry(state.value.country.value) },
            getState = { state.value.addEditCountry },
            setState = { state.value = state.value.copy(addEditCountry = it) },
            onSuccess = { ta ->
                state.update {
                    it.copy(
                        getCountry = State.success(ta),
                    )
                }

                state.value.country.value = ta

                toastManager.success(
                    description = "Country updated successfully",
                )
            },
        )

    private fun deleteCountry() =
        getData(
            getData = { countryRepository.delete(state.value.country.value) },
            getState = { state.value.deleteCountry },
            setState = { state.value = state.value.copy(deleteCountry = it) },
            onSuccess = {
                toastManager.success(
                    description = "Country deleted successfully",
                )
            },
        )

    private fun clearState() {
        state.value = state.value.copy(
            getCountry = State(),
            addEditCountry = State(),
            deleteCountry = State(),
            getAreasCountryCount = State(),
            isEditEnabled = false,
        )

        state.value.country.value = CountryModel.empty
    }
}